import React, { useContext, useState, useEffect } from 'react';
import styles from './ReservationDetails.module.css';
import { Helmet } from 'react-helmet';
import { useLocation, useParams } from 'react-router-dom';
import Header from '../../components/header/Header';
import AppInfo from '../../components/appInfo/AppInfo';
import Sidebar from '../../components/sidebar/Sidebar';
import ReservationPropertyDetails from '../../components/reservationPropertyDetails/ReservationPropertyDetails';
import { useOktaAuth } from '@okta/okta-react';
import { reformatReservationId, reformatName } from '../../utils/utils';
import ChangeRequest from '../../components/changeRequest/ChangeRequest';
import ChangeRequestList from '../../components/changeRequestList/ChangeRequestList';
import { IChangeRequest } from '../../interfaces';
import AlertContent from '../../components/alertContent/AlertContent';
import { modes } from '../../utils/contants';
import { trackPromise } from 'react-promise-tracker';
import { getReservationDetailsByReservationId } from '../../apiCall';
import CrisisToolStatusContext from '../../context/CrisisToolStatusContext';

const ReservationDetails = () => {
  const [reservationDetail, setReservationDetail] = useState(Object);
  const reservationPathName = useLocation().pathname;
  const { reservationId } = useParams();
  const { oktaAuth, authState } = useOktaAuth();
  const { markCrisisToolDeactivated } = useContext(CrisisToolStatusContext);
  const [selectedRequest, setSelectedRequest] = useState<IChangeRequest | null>(null);
  const [crMode, setcrMode] = useState(modes.ADD);
  const [showChangeRequest, setShowChangeRequest] = useState(false);
  const [showAlert, setShowAlert] = useState({
    isAlert: false,
    alertType: ''
  });
  const [isPinCodeCorrect, setIsPinCodeCorrect] = useState(false);
  const [validatedPinNumber, setValidatedPinNumber] = useState('');
  // Get email from sessionStorage (set by SortTable before opening new tab)
  const decodedEmailId = sessionStorage.getItem('bookerEmail') || '';
  const showChangeRequestComponent = () => {
    setcrMode(modes.ADD);
    setShowChangeRequest(true);
  };
  const onEditHandler = (request: IChangeRequest) => {
    setcrMode(modes.EDIT);
    setSelectedRequest(request);
    setShowChangeRequest(true);
  };

  const alertHandler = (flag: boolean, alertType: string) => {
    setShowAlert({ isAlert: flag, alertType: alertType });
  };

  const toggleChangeRequest = (flag: boolean) => {
    setShowChangeRequest(!flag);
  };

  const pinValidationHandler = (flag: boolean, pinNumber: string) => {
    setIsPinCodeCorrect(flag);
    setValidatedPinNumber(pinNumber);
  };

  useEffect(() => {
    (async () => {
      if (authState?.isAuthenticated === false) {
        await oktaAuth.signInWithRedirect({});
      }
    })();
  }, [oktaAuth, authState?.isAuthenticated]);

  useEffect(() => {
    (async () => {
      if (authState?.isAuthenticated) {
        await trackPromise(
          getReservationDetailsByReservationId({
            reservationId: reservationId as string,
            bookerEmail: decodedEmailId
          })
            .then((response) => {
              if (response.status === 403) {
                markCrisisToolDeactivated();
              } else if (response.status === 200) {
                setReservationDetail(response.data);
              }
            })
            .catch((error) => {
              error && setReservationDetail({});
            })
        );
      }
    })();
  }, [reservationId, authState?.isAuthenticated]);

  if (!authState || authState?.isAuthenticated === false) {
    return null;
  }

  return (
    <div
      data-testid={'reservationDetailsTestId'}
      className={styles.reservationDetailsOuterContainer}>
      {reservationDetail.reservationDetails && (
        <Helmet>
          <title>{`${reformatName(
            reservationDetail.reservationDetails[0].bookerFirstName,
            reservationDetail.reservationDetails[0].bookerLastName
          )} - ${reservationId && reformatReservationId(reservationId)} • Booking.com`}</title>
        </Helmet>
      )}
      <header>
        <Header />
        <AppInfo />
      </header>
      <div className={styles.reservationDetailsContainer}>
        <Sidebar
          csAgentPathName={reservationPathName}
          onCRButtonClick={showChangeRequestComponent}
          changeRequestButtonDisabled={!isPinCodeCorrect || showChangeRequest}
        />
        {reservationDetail.reservationDetails && (
          <div
            className={styles.reservationPropertyDetailsContainer}
            data-testid={'reservationPropertyDetailsTestId'}>
            {showAlert.isAlert && <AlertContent alertType={showAlert.alertType} mode={crMode} />}
            {showChangeRequest && (
              <ChangeRequest
                request={selectedRequest}
                mode={crMode}
                reservationId={reservationId?.replace(/\./g, '') ?? ''}
                onAlertHandler={alertHandler}
                hideComponent={toggleChangeRequest}
                validatedPinNumber={validatedPinNumber}
              />
            )}
            <ReservationPropertyDetails
              details={reservationDetail.reservationDetails[0]}
              isPinCodeCorrectHandler={pinValidationHandler}
            />
            <ChangeRequestList
              renderagain={showAlert.isAlert}
              onEditHandler={onEditHandler}
              reservationId={reservationId?.replace(/\./g, '') ?? ''}
              isPinCodeCorrect={!isPinCodeCorrect}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReservationDetails;
